import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getValidationBindingsByTable } from '@/api/endpoints/validationRules'
import type { ValidationRuleResponse } from '@/api/endpoints/validationRules'

/**
 * Normaliserer regeltype fra camelCase (backend) til PascalCase (frontend).
 */
function normalizeRuleType (ruleType: string): string {
  const mapping: Record<string, string> = {
    allowedValues: 'AllowedValues',
    minValue: 'MinValue',
    maxValue: 'MaxValue',
    minLength: 'MinLength',
    maxLength: 'MaxLength',
    required: 'Required',
    regex: 'Regex',
    dateBefore: 'DateBefore',
    dateAfter: 'DateAfter',
    unique: 'Unique',
    phoneNumber: 'PhoneNumber',
    ean13: 'Ean13',
    ean15: 'Ean15',
    vatNumber: 'VatNumber',
    conditionalRequired: 'ConditionalRequired',
    atLeastOneRequired: 'AtLeastOneRequired',
    conditionalAllowedValues: 'ConditionalAllowedValues',
    groupExists: 'GroupExists'
  }
  return mapping[ruleType] || ruleType
}

/**
 * Pinia store for validation rules.
 * Henter og cacher alle regler for én tabell om gangen.
 * Når tableKey endres, erstattes hele arrayet.
 */
export const useValidationRulesStore = defineStore('validationRules', () => {
  // Enkel array - vi har bare én tabell i minnet om gangen
  const rules = ref<ValidationRuleResponse[] | null>(null)
  const currentTableKey = ref<string | null>(null)

  /**
   * Henter alle regler for en tableKey.
   * Erstatter hele rules-arrayet når tableKey endres.
   */
  async function loadBindingsForTable (tableKey: string): Promise<ValidationRuleResponse[]> {
    const loadedRules = await getValidationBindingsByTable(tableKey)
    rules.value = loadedRules
    return loadedRules
  }

  /**
   * Henter AllowedValues for en spesifikk kolonne.
   * Søker i den ene rules-arrayen vi har.
   */
  function getAllowedValuesForColumn (columnName: string): string[] | null {
    if (!rules.value) {
      return null
    }

    const allowedValuesRule = rules.value.find((rule: ValidationRuleResponse) => {
      if (!rule.isActive) {
        return false
      }
      // ruleType kan være string fra JSON (camelCase) eller undefined
      const ruleType = rule.ruleType
      if (!ruleType || typeof ruleType !== 'string') {
        return false
      }
      const normalizedRuleType = normalizeRuleType(ruleType)
      if (normalizedRuleType !== 'AllowedValues') return false
      // Case-insensitive matching for kolonnenavn
      return rule.columnName.toLowerCase() === columnName.toLowerCase()
    })

    if (!allowedValuesRule) {
      return null
    }

    try {
      const params = allowedValuesRule.parameters
      if (!params || typeof params !== 'object') {
        return null
      }
      const values = params.values
      if (!Array.isArray(values)) {
        return null
      }
      return values.map(String)
    } catch {
      return null
    }
  }

  /**
   * Sjekker om en verdi er gyldig for en kolonne (for validering i tabell).
   * Returnerer true hvis gyldig, false hvis ugyldig, null hvis ingen regel finnes.
   */
  function isValidValueForColumn (columnName: string, value: any): boolean | null {
    const allowedValues = getAllowedValuesForColumn(columnName)
    if (!allowedValues) {
      return null // Ingen regel
    }
    if (value === null || value === undefined || value === '') {
      return true // Tom verdi er OK
    }
    const normalizedValue = String(value).trim().toLowerCase()
    return allowedValues.some(v => String(v).trim().toLowerCase() === normalizedValue)
  }

  /**
   * Henter MinValue for en spesifikk kolonne.
   */
  function getMinValueForColumn (columnName: string): number | null {
    if (!rules.value) {
      return null
    }

    const minRule = rules.value.find((rule: ValidationRuleResponse) => {
      if (!rule.isActive) {
        return false
      }
      const ruleType = rule.ruleType
      if (!ruleType || typeof ruleType !== 'string') {
        return false
      }
      const normalizedRuleType = normalizeRuleType(ruleType)
      if (normalizedRuleType !== 'MinValue') return false
      return rule.columnName.toLowerCase() === columnName.toLowerCase()
    })

    if (!minRule?.parameters) {
      return null
    }

    const value = minRule.parameters.minNumericValue
    if (typeof value === 'number') {
      return value
    }
    if (typeof value === 'string') {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : null
    }

    return null
  }

  /**
   * Henter MaxValue for en spesifikk kolonne.
   */
  function getMaxValueForColumn (columnName: string): number | null {
    if (!rules.value) {
      return null
    }

    const maxRule = rules.value.find((rule: ValidationRuleResponse) => {
      if (!rule.isActive) {
        return false
      }
      const ruleType = rule.ruleType
      if (!ruleType || typeof ruleType !== 'string') {
        return false
      }
      const normalizedRuleType = normalizeRuleType(ruleType)
      if (normalizedRuleType !== 'MaxValue') return false
      return rule.columnName.toLowerCase() === columnName.toLowerCase()
    })

    if (!maxRule?.parameters) {
      return null
    }

    const value = maxRule.parameters.maxNumericValue
    if (typeof value === 'number') {
      return value
    }
    if (typeof value === 'string') {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : null
    }

    return null
  }

  /**
   * Setter current tableKey og laster regler hvis nødvendig.
   * Kalles når man navigerer til en ny tabell.
   */
  async function setTableKey (tableKey: string | null) {
    const normalizedKey = String(tableKey || '').trim() || null
    if (normalizedKey === currentTableKey.value) {
      return
    }

    currentTableKey.value = normalizedKey

    if (normalizedKey) {
      await loadBindingsForTable(normalizedKey)
    } else {
      rules.value = null
    }
  }

  /**
   * Clear cache (brukes når regler oppdateres).
   */
  function clearCache (tableKey?: string) {
    const normalizedKey = String(tableKey || '').trim()
    if (!normalizedKey || currentTableKey.value === normalizedKey) {
      rules.value = null
      currentTableKey.value = null
    }
  }

  return {
    currentTableKey: computed(() => currentTableKey.value),
    loadBindingsForTable,
    getAllowedValuesForColumn,
    getMinValueForColumn,
    getMaxValueForColumn,
    isValidValueForColumn,
    setTableKey,
    clearCache
  }
})
